/**
 * ExportVisualizerDialog — the pre-export popup shared by every editor.
 *
 * Before writing the shareable map file (.synth / .flow), this asks whether to
 * bundle any visualizer(s) from the library INTO the file (so the game can read
 * them). Presentational + self-contained: it reads the visualizer library from
 * vizStore, lets the user tick which ones to include, and on Export hands the
 * caller the raw .vizproject/.visualizer JSON of each selection to embed.
 */

import React, { useState } from 'react';
import { color, font } from './theme';
import { useVizStore, getVizRawJson } from '../../viz/vizStore';

export interface BundledVisualizer { name: string; json: string }

interface Props {
  open: boolean;
  /** Shown in the header (e.g. the song / map name). */
  mapName?: string;
  onCancel: () => void;
  /** Selected visualizers' raw JSON, ready to embed. Empty = export with none. */
  onConfirm: (visualizers: BundledVisualizer[]) => void;
}

/** Sanitize a library name into a safe `.vizproject` filename. */
function vizFilename(name: string): string {
  const base = (name || 'visualizer').replace(/\.(vizproject|visualizer)$/i, '');
  const safe = base.replace(/[^\w\-. ]+/g, '_').replace(/\s+/g, '_') || 'visualizer';
  return `${safe}.vizproject`;
}

export default function ExportVisualizerDialog({ open, mapName, onCancel, onConfirm }: Props) {
  const library = useVizStore(s => s.library);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const toggle = (id: string) => setChecked(c => ({ ...c, [id]: !c[id] }));
  const selectedIds = library.filter(v => checked[v.id]).map(v => v.id);

  const doExport = async () => {
    setBusy(true);
    try {
      const bundled: BundledVisualizer[] = [];
      const usedNames = new Set<string>();
      for (const item of library) {
        if (!checked[item.id]) continue;
        const json = await getVizRawJson(item.id);
        if (!json) continue;
        // De-dup filenames so two items with the same name don't collide in the zip.
        let fname = vizFilename(item.name);
        let n = 2;
        while (usedNames.has(fname.toLowerCase())) fname = vizFilename(`${item.name}_${n++}`);
        usedNames.add(fname.toLowerCase());
        bundled.push({ name: fname, json });
      }
      onConfirm(bundled);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={backdrop} onClick={() => !busy && onCancel()}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={title}>Export map{mapName ? ` — ${mapName}` : ''}</div>
        <div style={sub}>
          Add a visualizer to the file? The selected visual(s) are bundled inside
          the map so they travel with it and can be opened in the game.
        </div>

        {library.length === 0 ? (
          <div style={emptyStyle}>
            No visualizers in your library yet — the map will export on its own.
          </div>
        ) : (
          <div style={listStyle}>
            {library.map(item => (
              <label key={item.id} style={rowStyle}>
                <input
                  type="checkbox"
                  checked={!!checked[item.id]}
                  onChange={() => toggle(item.id)}
                  style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                />
                <span style={nameStyle}>{item.name}</span>
                <span style={kindStyle}>{item.kind === 'project' ? '.vizproject' : '.visualizer'}</span>
              </label>
            ))}
          </div>
        )}

        <div style={btnRow}>
          <button style={cancelBtn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={exportBtn} onClick={doExport} disabled={busy}>
            {busy ? 'Exporting…'
              : selectedIds.length > 0 ? `Export with ${selectedIds.length} visual${selectedIds.length > 1 ? 's' : ''}`
              : 'Export without visual'}
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 10000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'oklch(0 0 0 / 0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
};
const card: React.CSSProperties = {
  minWidth: 'min(340px, calc(100vw - 24px))', maxWidth: 'min(460px, calc(100vw - 24px))',
  maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', padding: '24px 26px', borderRadius: 18,
  background: 'oklch(0.11 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.72)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.25)',
  boxShadow: '0 12px 48px rgba(0,0,0,0.6), 0 0 30px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.10)',
  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  color: color.ink, fontFamily: font.sans,
};
const title: React.CSSProperties = { fontSize: 16, fontWeight: 700, letterSpacing: 0.3, textAlign: 'center' };
const sub: React.CSSProperties = { marginTop: 8, fontSize: 12, color: color.inkDim, lineHeight: 1.45, textAlign: 'center' };
const emptyStyle: React.CSSProperties = { marginTop: 16, fontSize: 12, color: color.inkDim, textAlign: 'center', fontStyle: 'italic' };
const listStyle: React.CSSProperties = { marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' };
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 10, cursor: 'pointer',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
};
const nameStyle: React.CSSProperties = { flex: 1, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const kindStyle: React.CSSProperties = { fontSize: 10, color: '#8A82B0', fontFamily: font.sans };
const btnRow: React.CSSProperties = { marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' };
const cancelBtn: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.2)',
  background: 'transparent', color: '#8A82B0', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: font.sans,
};
const exportBtn: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 999,
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.20)',
  color: color.ink, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: font.sans,
};
